import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useAuth from '../hooks/useAuth';
import { getTokenUsage, getUserTokenUsageDetail } from '../api/chatHistoryAPIs';
import { appLocale } from '../utils/dateFormat';

import '../css/pages/token_usage.css';

const PAGE_SIZE = 10;
// Kompaktný formát — token_usage je kumulatívny (bez resetu), tak ho skrátime na k/M,
// aby čísla časom neboli obrovské. Plná hodnota je v tooltipe (fmtFull).
const fmt = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return (v / 1_000_000).toLocaleString(appLocale(), { maximumFractionDigits: 1 }) + ' M';
  if (v >= 1_000) return (v / 1_000).toLocaleString(appLocale(), { maximumFractionDigits: 1 }) + ' k';
  return v.toLocaleString(appLocale());
};
const fmtFull = (n) => (Number(n) || 0).toLocaleString(appLocale());

const fmtDateTime = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = (x) => String(x).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const UserSessions = ({ userId }) => {
  const [t] = useTranslation('global');
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getUserTokenUsageDetail(userId)
      .then((data) => { if (active) setSessions(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setSessions([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [userId]);

  if (loading) {
    return (
      <div className="lds-ellipsis tu-mini-loader"><div></div><div></div><div></div><div></div></div>
    );
  }
  if (!sessions || sessions.length === 0) {
    return <p className="tu-empty">{t('noUsage')}</p>;
  }

  const pageCount = Math.ceil(sessions.length / PAGE_SIZE);
  const paged = sessions.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const intentBadge = (intent) => {
    if (intent === 'PromptEmbeddings') return <span className="tu-src tu-src-docs">{t('documents')}</span>;
    if (intent === 'PromptGpt') return <span className="tu-src tu-src-chat">{t('tabChat')}</span>;
    if (intent === 'PromptData') return <span className="tu-src tu-src-data">{t('data')}</span>;
    return <span className="tu-src tu-src-none">—</span>;
  };

  return (
    <div className="tu-detail">
      <div className="tu-table-scroll">
        <table className="tu-table">
          <thead>
            <tr>
              <th>{t('date')}</th>
              <th>{t('usageType')}</th>
              <th className="tu-num">{t('promptTokens')}</th>
              <th className="tu-num">{t('completionTokens')}</th>
              <th className="tu-num">{t('embeddingTokens')}</th>
              <th className="tu-num">{t('totalTokens')}</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((s, idx) => {
              const total = (s.promptTokens || 0) + (s.completionTokens || 0) + (s.embeddingTokens || 0);
              return (
                <tr key={s.sessionId ?? idx}>
                  <td>{fmtDateTime(s.lastAt)}</td>
                  <td>{intentBadge(s.intent)}</td>
                  <td className="tu-num" title={fmtFull(s.promptTokens)}>{fmt(s.promptTokens)}</td>
                  <td className="tu-num" title={fmtFull(s.completionTokens)}>{fmt(s.completionTokens)}</td>
                  <td className="tu-num" title={fmtFull(s.embeddingTokens)}>{fmt(s.embeddingTokens)}</td>
                  <td className="tu-num tu-total" title={fmtFull(total)}>{fmt(total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="tu-pager">
          <button
            type="button"
            className="tu-pager-btn"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            <span className="material-symbols-outlined">chevron_left</span>
          </button>
          <span className="tu-pager-info">{page + 1} / {pageCount}</span>
          <button
            type="button"
            className="tu-pager-btn"
            disabled={page >= pageCount - 1}
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>
      )}
    </div>
  );
};

const TokenUsage = () => {
  const { auth } = useAuth();
  const [t] = useTranslation('global');
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (Number(auth?.uri) !== 1) {
      navigate('/Unauthorized');
    }
  }, [auth?.uri, navigate]);

  useEffect(() => {
    setIsLoading(true);
    getTokenUsage()
      .then((data) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setIsLoading(false));
  }, []);

  const handleGoBack = () => navigate('/');

  const toggle = useCallback((userId) => {
    setExpanded((prev) => (prev === userId ? null : userId));
  }, []);

  const term = search.trim().toLowerCase();
  const filtered = term
    ? rows.filter((r) =>
        `${r.userFirstname || ''} ${r.userLastname || ''}`.toLowerCase().includes(term) ||
        (r.userEmail || '').toLowerCase().includes(term))
    : rows;

  return (
    <div className="page-wrap all-users-list token-usage-page">
      <button className="return-button return-button-top" onClick={handleGoBack}>
        <span className="material-symbols-outlined">arrow_back</span>{t('return')}
      </button>

      <div className="header-title">
        <h1>{t('tokenUsage')}</h1>
      </div>

      <div className="user-searchbar tu-searchbar show">
        <span className="material-symbols-outlined">search</span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('searchByUsername')}
        />
      </div>

      <div className={`user-detail-wrapper ${isLoading ? 'center-loader' : ''}`}>
        {isLoading ? (
          <div className="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
        ) : filtered.length === 0 ? (
          <p className="user-detail no-users-message tu-empty">{t('noResults')}</p>
        ) : (
          filtered.map((r) => {
            const isOpen = expanded === r.userId;
            return (
              <div key={r.userId} className={`tu-user ${isOpen ? 'open' : ''}`}>
                <div className="user-detail tu-row" onClick={() => toggle(r.userId)}>
                  <div className="user-icon">
                    {(r.userFirstname || '?').charAt(0)}{(r.userLastname || '').charAt(0)}
                  </div>
                  <span className="user-name-label">
                    {`${r.userFirstname || ''} ${r.userLastname || ''}`.trim() || r.userEmail}
                  </span>
                  <span className="user-email-label">{r.userEmail}</span>
                  <div className="tu-stats">
                    <span className="tu-badge tu-badge-sessions" title={t('sessionCount')}>
                      <span className="material-symbols-outlined">forum</span>{r.sessionCount}
                    </span>
                    <span className={`material-symbols-outlined tu-chevron ${isOpen ? 'open' : ''}`}>
                      expand_more
                    </span>
                  </div>
                </div>

                {isOpen && <UserSessions userId={r.userId} />}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default TokenUsage;
